/**
 * The unified inbox's rail folder, as a URL: `?inbox_folder=`.
 *
 * ## The URL is the folder's only home
 *
 * `UnifiedInboxSurface` used to hold the folder in component state, seeded once
 * from a one-shot link (#803) that it then stripped. Opening a message
 * unmounts the surface, so Back — like a reload or a shared link — remounted it
 * on the Inbox whatever folder the reader had been in. The surface now DERIVES
 * its folder from this param and writes it (replace, not push: a folder click
 * is not a place in history, same rule as the filters) on every rail click, so
 * the list URL always says where the list is.
 *
 * - The default, `inbox`, is never written: an untouched inbox stays `/inbox`.
 * - A folder the rail does not offer resolves to Inbox and the URL is left
 *   alone, so a stale bookmark can neither blank the list nor be rewritten.
 * - The switcher builds a fresh `/inbox?inbox=<id>`, so changing inbox drops the
 *   folder by construction — there is no state to go stale.
 * - The bell's "In Trash" entry (#803) is just one producer: `inboxTrashLink`.
 *
 * It is deliberately not `folder`: the personal surface reads `folder`
 * (`personalInboxUrl.js`), and the personal surface is what renders while
 * inboxes load or when the requested inbox is gone — it must not open the
 * user's OWN Trash instead. (The reader's `?folder=trash` is a third meaning —
 * "read this conversation through the Trash" — and is not this param.)
 *
 * ## One vocabulary
 *
 * Valid folders are whatever the rail reports (`GET /api/mail/folders`, built
 * from `services/mail_folders.py`), read through `railViewIds` — there is no
 * second list here to drift from it.
 */

export const INBOX_FOLDER_PARAM = 'inbox_folder';

/** The folder an untouched inbox shows; never written to the URL. */
export const DEFAULT_INBOX_FOLDER = 'inbox';

/** `/inbox?inbox=<id>`, on `folder` when it is not the default. */
export function inboxFolderLink(inboxId, folder) {
    const base = `/inbox?inbox=${encodeURIComponent(inboxId)}`;
    return folder && folder !== DEFAULT_INBOX_FOLDER
        ? `${base}&${INBOX_FOLDER_PARAM}=${encodeURIComponent(folder)}`
        : base;
}

/** The bell's link to one shared inbox's Trash list. */
export function inboxTrashLink(inboxId) {
    return inboxFolderLink(inboxId, 'trash');
}

/**
 * Every folder id a rail model can route to, or null while the rail has not
 * answered. System rows and group children are destinations; group ids and
 * tags are not. Null — not an empty set — for an absent or empty model: an
 * empty set would read as "no folder is valid" and send every link to Inbox.
 */
export function railViewIds(model) {
    if (!model) return null;
    const ids = new Set((model.system || []).map(item => item.id));
    for (const group of model.groups || []) {
        for (const child of group.children || []) ids.add(child.id);
    }
    return ids.size ? ids : null;
}

/**
 * The folder a `location.search` asks for, as a view id.
 *
 * @param viewIds  `railViewIds(model)`. Null (the rail has not answered, or
 *   failed) trusts the URL: the list endpoint is the authority there, and a
 *   deep link should not wait on the rail's count query to open.
 */
export function resolveInboxFolder(search, viewIds) {
    const requested = new URLSearchParams(search || '').get(INBOX_FOLDER_PARAM);
    if (!requested) return DEFAULT_INBOX_FOLDER;
    if (viewIds && !viewIds.has(requested)) return DEFAULT_INBOX_FOLDER;
    return requested;
}

/** `search` with the folder written (or, for the default, removed), as a `?…` string. */
export function searchWithFolder(search, folder) {
    const params = new URLSearchParams(search || '');
    if (folder && folder !== DEFAULT_INBOX_FOLDER) params.set(INBOX_FOLDER_PARAM, folder);
    else params.delete(INBOX_FOLDER_PARAM);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

/**
 * The query string of a reader URL (`/inbox/:inboxId/:conversationId`).
 *
 * `inTrash` is the reader's own switch (`folder=trash`: read the conversation as
 * it sits in this inbox's Trash). `listFolder` is the list it was opened from,
 * which the reader needs only to find its way back when there is no history
 * entry to pop (a pasted link, a new tab).
 */
export function readerSearch({ inTrash = false, listFolder = null } = {}) {
    const params = new URLSearchParams();
    if (inTrash) params.set('folder', 'trash');
    if (listFolder && listFolder !== DEFAULT_INBOX_FOLDER) {
        params.set(INBOX_FOLDER_PARAM, listFolder);
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

/**
 * The list folder a reader URL leads back to, or null for the Inbox. A link
 * from before the list handed its folder over knows only `folder=trash`.
 */
export function readerBackFolder(searchParams) {
    return searchParams.get(INBOX_FOLDER_PARAM)
        || (searchParams.get('folder') === 'trash' ? 'trash' : null);
}

/**
 * The Inbox page's tab-to-URL sync: the params to write, or null when the URL
 * already says `activeTab`. Every other param — the inbox, the folder, the
 * filters — is carried through untouched.
 */
export function tabSyncParams(searchParams, activeTab) {
    const currentTab = searchParams.get('tab') || 'primary';
    if (currentTab === activeTab) return null;
    const next = new URLSearchParams(searchParams);
    if (activeTab === 'primary') next.delete('tab');
    else next.set('tab', activeTab);
    return next;
}
